<template>
  <div id="display-panels" class="my-4 bg-white shadow rounded">
    <b-tabs v-model="selectedIndex" content-class="mt-3 py-4">
      <b-tab v-for="(tab, index) in tabs" :key="tab.title" :title="tab.title">
        <template #title>
          {{ tab.title }}
          <span v-if="index === 1 && hasError" class="vertical-middle text-danger"
            ><WarningIcon></WarningIcon
          ></span>
        </template>
        <div class="tab-content">
          <div v-if="tab.index === 0">
            <ModelInfo :model="selectedModel" />
          </div>
          <div v-else-if="tab.index === 1">
            <DosesTable :model="selectedModel" />
            <div v-if="showGraph">
              <DosePlanGraph
                id="PatientDataGraph"
                :dose-plan="null"
                :show-secondary="showSecondary"
                chart-id="PatientData"
              />
            </div>
          </div>
          <div v-else-if="tab.index === 2">
            <div v-if="simulation" class="flex flex-column">
              <PlanOptions />
              <Recommendation />
              <div v-if="showGraph">
                <DosePlanGraph
                  id="SimulationGraph"
                  :dose-plan="selectedPlan"
                  :show-secondary="showSecondary"
                  chart-id="Simulation"
                />
              </div>
              <div v-else>
                <p>Select a dosing plan to see graph.</p>
              </div>
            </div>
            <div v-else>
              <p>
                To run the precision dosing calculations, select a drug model, and enter patient
                data.
              </p>
            </div>
          </div>
          <div v-else-if="tab.index === 3">
            <SavedData :update="update" />
          </div>
        </div>
      </b-tab>
    </b-tabs>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import DosesTable from '@/components/display/DosesTable.vue';
import PlanOptions from '@/components/display/PlanOptions.vue';
import Recommendation from '@/components/display/Recommendation.vue';
import DosePlanGraph from '@/components/display/DosePlanGraph.vue';
import SavedData from '@/components/display/SavedData.vue';
import ModelInfo from '@/components/display/ModelInfo.vue';
import WarningIcon from 'vue-material-design-icons/Alert.vue';
import { useDosingStore } from '@/stores/dosing';

defineOptions({ name: 'TabPanels' });

const props = defineProps<{
  update?: string;
  activeTab?: number;
}>();

const store = useDosingStore();
const selectedIndex = ref(0);

const tabs = [
  { index: 0, title: 'MODEL INFO' },
  { index: 1, title: 'PATIENT DATA' },
  { index: 2, title: 'SIMULATION RESULTS' },
  { index: 3, title: 'SAVED PATIENTS' },
];

watch(
  () => props.activeTab,
  (val) => {
    if (val !== undefined) selectedIndex.value = val;
  },
);

const simulation = computed(() => store.getSimulation);
const selectedPlan = computed(() => store.getSelectedPlan);
const selectedModel = computed(() => store.getModel);
const showGraph = computed(() => selectedPlan.value || simulation.value?.hasAdministeredDoses);
const showSecondary = true;
const hasError = computed(() => store.getDoses.filter((d: any) => d.sinceAlert).length > 0);
</script>

<style scoped>
#display-panels {
  border: 1pt solid #e7e4e4;
  padding-top: 10px;
}

.tab-content {
  min-height: 650px;
}
</style>
